import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import LicenseDetailsModal from '../../components/LicenseDetailsModal';
import { isLicenseExpired, formatLicensePeriod } from './licenseUtils';

const LicenseList = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingLicenseId, setViewingLicenseId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  useEffect(() => {
    loadLicenses();
  }, [searchParams]);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (searchParams.get('expiring_soon') === 'true') {
        filters.expiring_soon = 'true';
      }
      if (searchParams.get('status')) {
        filters.status = searchParams.get('status');
      }
      if (searchTerm) {
        filters.search = searchTerm;
      }

      const response = await api.getLicenses(filters);
      if (response.success) {
        setLicenses(response.data || []);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadLicenses();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this license?')) {
      return;
    }

    try {
      const response = await api.deleteLicense(id);
      if (response.success) {
        loadLicenses();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleActivateQuick = async (license) => {
    const start = window.prompt('Start date (YYYY-MM-DD)', new Date().toISOString().split('T')[0]);
    if (!start) return;
    const end = window.prompt('End date (YYYY-MM-DD)', '');
    if (!end) return;

    setActivatingId(license.id);
    setError('');
    try {
      const response = await api.activateLicense(license.id, {
        start_date: start,
        expiry_date: end,
      });
      if (response.success) {
        if (response.demo_credentials?.password) {
          window.alert(`License activated. New password: ${response.demo_credentials.password}`);
        }
        loadLicenses();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActivatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-success',
      expiring: 'bg-warning',
      expired: 'bg-danger',
      suspended: 'bg-secondary',
      cancelled: 'bg-dark',
    };
    return badges[status] || 'bg-secondary';
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <Link to="/licenses/create" className="btn btn-primary btn-sm mr-2">
            <i className="fas fa-plus"></i> Create License
          </Link>
          <Link to="/licenses/create?type=demo" className="btn btn-info btn-sm">
            <i className="fas fa-flask"></i> Create Demo License
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Licenses</h3>
              <div className="card-tools">
                <form onSubmit={handleSearch} className="input-group input-group-sm" style={{ width: 200 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-default" type="submit">
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="card-body">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Company</th>
                    <th>License Key</th>
                    <th>Status</th>
                    <th>Active Period</th>
                    <th>Users</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.length > 0 ? (
                    licenses.map(license => (
                      <tr key={license.id}>
                        <td>{license.id}</td>
                      <td>{license.company_name}</td>
                      <td>
                        <code>{license.license_key}</code>
                        {license.license_type === 'demo' && (
                          <span className="badge bg-info ml-1" style={{ fontSize: '10px' }}>Demo</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(license.status)}`}>
                          {license.status}
                        </span>
                      </td>
                      <td>
                        <div>
                          {license.tier_name && (
                            <span className="badge bg-secondary mr-1" style={{ fontSize: '10px' }}>
                              {license.tier_name}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', marginTop: '2px' }}>
                          {formatLicensePeriod(license)}
                          {isLicenseExpired(license) && (
                            <span className="badge bg-danger ml-1" style={{ fontSize: '9px' }}>Expired</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{license.current_users || 0}</strong> / {license.max_users === 999 ? '∞' : license.max_users || 1}
                        </div>
                        {license.max_users > 0 && (
                          <div className="progress mt-1" style={{ height: '8px', width: '60px' }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{
                                width: `${Math.min(((license.current_users || 0) / license.max_users) * 100, 100)}%`
                              }}
                            ></div>
                          </div>
                        )}
                      </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-primary mr-1" 
                            onClick={() => setViewingLicenseId(license.id)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          {isLicenseExpired(license) && (
                            <button
                              className="btn btn-sm btn-warning mr-1"
                              title="Activate"
                              disabled={activatingId === license.id}
                              onClick={() => handleActivateQuick(license)}
                            >
                              <i className="fas fa-redo"></i>
                            </button>
                          )}
                          <Link to={`/licenses/edit/${license.id}`} className="btn btn-sm btn-info mr-1" title="Edit">
                            <i className="fas fa-edit"></i>
                          </Link>
                          <button 
                            className="btn btn-sm btn-danger" 
                            onClick={() => handleDelete(license.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">No licenses found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {viewingLicenseId && (
        <LicenseDetailsModal
          licenseId={viewingLicenseId}
          onClose={() => setViewingLicenseId(null)}
        />
      )}
    </>
  );
};

export default LicenseList;


